import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { tApi } from "@/lib/api";
import { Loader2 } from "lucide-react";

const fieldCls =
  "w-full bg-white border border-black/10 rounded-2xl px-4 py-3.5 text-sm text-[#1A1A1A] placeholder:text-[#A3A3A3] focus:outline-none focus:border-[#A78BFA] focus:ring-2 focus:ring-[#A78BFA]/15 transition-colors";

// Avviso per chi ha già una sessione aperta in un altro centro AURA.
export function OtherTenantNotice({ slug }) {
  const { user, logout } = useAuth();
  if (!user || user.role === "super_admin" || !user.tenant_slug || user.tenant_slug === String(slug).toLowerCase()) {
    return null;
  }
  return (
    <div data-testid="other-tenant-notice" className="mb-5 text-sm text-[#4A4A4A] bg-[#F5F0FF] border border-[#E9D5FF] rounded-2xl px-4 py-3">
      Sei collegata a <strong>{user.tenant_name || user.tenant_slug}</strong>.
      Accedendo qui uscirai da quel centro.{" "}
      <button type="button" onClick={logout} className="underline underline-offset-2 text-[#1A1A1A]">Esci</button>
    </div>
  );
}

export default function LoginPage() {
  const { slug } = useParams();
  const { loginTenant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [tenantInfo, setTenantInfo] = useState(null);

  useEffect(() => {
    if (!slug) return;
    tApi(slug).publicInfo()
      .then(setTenantInfo)
      .catch(() => setTenantInfo({ name: "Centro non trovato", missing: true }));
  }, [slug]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const u = await loginTenant(slug, email.trim().toLowerCase(), password);
      const from = location.state?.from;
      const target = from || (u.role === "client" ? `/${slug}/app` : `/${slug}/staff`);
      navigate(target, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Credenziali non valide");
    } finally {
      setSubmitting(false);
    }
  };

  if (tenantInfo?.missing) {
    return (
      <AuthShell testId="login-page" subtitle="Centro non trovato" title="Questo centro non esiste" footer={
        <Link to="/" className="text-[#1A1A1A] underline underline-offset-4">Torna alla home</Link>
      }>
        <p className="text-[#4A4A4A]">Controlla il link che ti è stato inviato dal salone.</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      testId="login-page"
      subtitle={tenantInfo ? tenantInfo.name : "Bentornata"}
      title="Accedi al tuo salone."
      footer={
        <span>
          Non hai ancora un account?{" "}
          <Link to={`/${slug}/register`} data-testid="login-to-register" className="text-[#1A1A1A] underline underline-offset-4">
            Crea un account
          </Link>
        </span>
      }
    >
      <OtherTenantNotice slug={slug} />
      <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
        <label className="block">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">Email</span>
          <input data-testid="login-email" type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.it" className={fieldCls} />
        </label>
        <label className="block">
          <span className="block text-[10px] tracking-[0.25em] uppercase text-[#737373] mb-2">Password</span>
          <input data-testid="login-password" type="password" required value={password}
            onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={fieldCls} />
        </label>
        <div className="text-right -mt-1">
          <Link to={`/${slug}/forgot-password`} data-testid="login-forgot"
            className="text-xs text-[#737373] hover:text-[#1A1A1A] underline underline-offset-4">
            Password dimenticata?
          </Link>
        </div>
        {error && (
          <div data-testid="login-error" className="text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-2xl px-4 py-2.5">
            {error}
          </div>
        )}
        <button type="submit" data-testid="login-submit" disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] hover:bg-[#333] disabled:opacity-60 text-white rounded-full px-6 py-3.5 text-sm tracking-wide">
          {submitting && <Loader2 size={16} className="animate-spin" />}
          {submitting ? "Accesso…" : "Accedi"}
        </button>
        {/* Credenziali di prova: solo se il server le pubblica (DEMO_LOGIN_HINT),
            mai scritte nel codice. */}
        {tenantInfo?.demo_login_hint && (
          <p className="text-xs text-[#737373] text-center pt-2" data-testid="demo-login-hint">
            {tenantInfo.demo_login_hint}
          </p>
        )}
      </form>
    </AuthShell>
  );
}
